import React, { useState, useEffect } from 'react';
import Icon from 'components/AppIcon';
import Image from 'components/AppImage';

const ProjectLightbox = ({ project, currentLanguage, onClose, onShare }) => {
  const [showBefore, setShowBefore] = useState(true);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
    }, 200);
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const toggleImage = () => {
    setShowBefore(!showBefore);
  };

  const getServiceTypeLabel = (type) => {
    const labels = {
      'deep-cleaning': { es: 'Limpieza Profunda', en: 'Deep Cleaning' },
      'maintenance': { es: 'Mantenimiento', en: 'Maintenance' },
      'restoration': { es: 'Restauración', en: 'Restoration' },
      'commercial': { es: 'Comercial', en: 'Commercial' },
      'premium': { es: 'Premium', en: 'Premium' },
      'community': { es: 'Comunitario', en: 'Community' }
    };
    return labels[type] ? labels[type][currentLanguage] : type;
  };

  const getPoolSizeLabel = (size) => {
    const labels = {
      'small': { es: 'Pequeña', en: 'Small' },
      'medium': { es: 'Mediana', en: 'Medium' },
      'large': { es: 'Grande', en: 'Large' },
      'extra-large': { es: 'Extra Grande', en: 'Extra Large' }
    };
    return labels[size] ? labels[size][currentLanguage] : size;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return currentLanguage === 'es' ? date.toLocaleDateString('es-CR', { year: 'numeric', month: 'long', day: 'numeric' })
      : date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  return (
    <div
      className={`fixed inset-0 z-1200 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 ${
        isClosing ? 'opacity-0' : 'opacity-100'
      } transition-opacity duration-200`}
      onClick={handleBackdropClick}
    >
      <div
        className={`bg-surface rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto shadow-large ${
          isClosing ? 'scale-95 opacity-0' : 'scale-100 opacity-100'
        } transition-all duration-200`}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-2xl font-heading font-bold text-text-primary">
            {currentLanguage === 'es' ? project.title : project.titleEn}
          </h2>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => onShare(project)}
              className="p-2 rounded-lg text-text-secondary hover:text-primary hover:bg-primary/5 nav-transition"
              title={currentLanguage === 'es' ? 'Compartir' : 'Share'}
            >
              <Icon name="Share2" size={20} />
            </button>
            <button
              onClick={handleClose}
              className="p-2 rounded-lg text-text-secondary hover:text-primary hover:bg-primary/5 nav-transition"
              title={currentLanguage === 'es' ? 'Cerrar' : 'Close'}
            >
              <Icon name="X" size={20} />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-6">
          {/* Image Section */}
          <div className="space-y-4">
            {/* Main Image */}
            <div className="relative h-80 lg:h-96 overflow-hidden rounded-lg">
              <Image
                src={showBefore ? project.beforeImage : project.afterImage}
                alt={currentLanguage === 'es' ? project.title : project.titleEn}
                className="w-full h-full object-cover"
              />
              
              {/* Before/After Toggle */}
              <div className="absolute top-4 left-4">
                <button
                  onClick={toggleImage}
                  className={`px-4 py-2 rounded-full font-medium nav-transition ${
                    showBefore 
                      ? 'bg-error text-white' :'bg-success text-white'
                  }`}
                >
                  {showBefore 
                    ? (currentLanguage === 'es' ? 'Antes' : 'Before')
                    : (currentLanguage === 'es' ? 'Después' : 'After')
                  }
                </button>
              </div>

              {/* Rating */}
              <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm rounded-full px-3 py-2 flex items-center space-x-1">
                <Icon name="Star" size={16} className="text-warning fill-current" />
                <span className="font-medium">{project.rating}/5</span>
              </div>
            </div>

            {/* Image Toggle Controls */}
            <div className="flex space-x-2">
              <button
                onClick={() => setShowBefore(true)}
                className={`flex-1 py-2 px-4 rounded-lg font-medium nav-transition ${
                  showBefore
                    ? 'bg-error text-white' :'bg-gray-100 text-text-secondary hover:bg-gray-200'
                }`}
              >
                {currentLanguage === 'es' ? 'Ver Antes' : 'View Before'}
              </button>
              <button
                onClick={() => setShowBefore(false)}
                className={`flex-1 py-2 px-4 rounded-lg font-medium nav-transition ${
                  !showBefore
                    ? 'bg-success text-white' :'bg-gray-100 text-text-secondary hover:bg-gray-200'
                }`}
              >
                {currentLanguage === 'es' ? 'Ver Después' : 'View After'}
              </button>
            </div>
          </div>

          {/* Details Section */}
          <div className="space-y-6">
            {/* Project Info Grid */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-background p-4 rounded-lg">
                <div className="text-sm text-text-secondary mb-1">
                  {currentLanguage === 'es' ? 'Tipo de Servicio' : 'Service Type'}
                </div>
                <div className="font-semibold text-text-primary">
                  {getServiceTypeLabel(project.serviceType)}
                </div>
              </div>
              <div className="bg-background p-4 rounded-lg">
                <div className="text-sm text-text-secondary mb-1">
                  {currentLanguage === 'es' ? 'Tamaño de Piscina' : 'Pool Size'}
                </div>
                <div className="font-semibold text-text-primary">
                  {getPoolSizeLabel(project.poolSize)}
                </div>
              </div>
              <div className="bg-background p-4 rounded-lg">
                <div className="text-sm text-text-secondary mb-1">
                  {currentLanguage === 'es' ? 'Ubicación' : 'Location'}
                </div>
                <div className="font-semibold text-text-primary">{project.location}</div>
              </div>
              <div className="bg-background p-4 rounded-lg">
                <div className="text-sm text-text-secondary mb-1">
                  {currentLanguage === 'es' ? 'Duración' : 'Duration'}
                </div>
                <div className="font-semibold text-text-primary">
                  {currentLanguage === 'es' ? project.duration : project.durationEn}
                </div>
              </div>
            </div>

            {/* Description */}
            <div>
              <h3 className="text-lg font-heading font-semibold text-text-primary mb-3">
                {currentLanguage === 'es' ? 'Descripción del Proyecto' : 'Project Description'}
              </h3>
              <div className="prose prose-sm max-w-none text-text-secondary">
                <p className="whitespace-pre-line">
                  {currentLanguage === 'es' ? project.description : project.descriptionEn}
                </p>
              </div>
            </div>

            {/* Challenges */}
            <div>
              <h3 className="text-lg font-heading font-semibold text-text-primary mb-3">
                {currentLanguage === 'es' ? 'Desafíos Superados' : 'Challenges Overcome'}
              </h3>
              <div className="space-y-2">
                {(currentLanguage === 'es' ? project.challenges : project.challengesEn).map((challenge, index) => (
                  <div key={index} className="flex items-start space-x-2">
                    <Icon name="CheckCircle" size={16} className="text-success mt-0.5 flex-shrink-0" />
                    <span className="text-text-secondary text-sm">{challenge}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Customer Info */}
            <div className="bg-background p-4 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-text-secondary mb-1">
                    {currentLanguage === 'es' ? 'Cliente' : 'Customer'}
                  </div>
                  <div className="font-semibold text-text-primary">{project.customerName}</div>
                </div>
                <div className="text-right">
                  <div className="text-sm text-text-secondary mb-1">
                    {currentLanguage === 'es' ? 'Completado' : 'Completed'}
                  </div>
                  <div className="font-semibold text-text-primary">
                    {formatDate(project.completionDate)}
                  </div>
                </div>
              </div>
            </div>

            {/* Call to Action */}
            <div className="bg-gradient-to-r from-primary/10 to-secondary/10 p-6 rounded-lg">
              <h4 className="font-heading font-semibold text-text-primary mb-2">
                {currentLanguage === 'es' ?'¿Te gustó este proyecto?' :'Like this project?'
                }
              </h4>
              <p className="text-text-secondary text-sm mb-4">
                {currentLanguage === 'es' ?'Contáctanos para obtener una cotización gratuita para tu piscina.' :'Contact us to get a free quote for your pool.'
                }
              </p>
              <div className="flex flex-col sm:flex-row gap-2">
                <a
                  href="tel:+50688888888"
                  className="flex items-center justify-center space-x-2 bg-primary text-white px-4 py-2 rounded-lg font-medium hover:bg-primary/90 nav-transition"
                >
                  <Icon name="Phone" size={16} />
                  <span>+506 8888-8888</span>
                </a>
                <a
                  href="https://wa.me/50688888888"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center space-x-2 bg-success text-white px-4 py-2 rounded-lg font-medium hover:bg-success/90 nav-transition"
                >
                  <Icon name="MessageCircle" size={16} />
                  <span>WhatsApp</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectLightbox;